/**
 * What Music DNA and the Genre Mixer show to an account without Lyrixa Composer+.
 *
 * Composer+ is OTO 2 and its two features have been named in this codebase since the
 * prototype — `composerPlus` in the entitlement model, "unlocks Music DNA and Genre
 * Mixer" in its doc comment, `// Music DNA (Composer+)` over the functions in
 * lib/music.ts, a product row on /products listing both by name — and nothing read any
 * of it. Both screens generated for anybody. This is the screen that stands in their
 * place instead.
 *
 * ONE COMPONENT FOR TWO SCREENS, and the heading is the only thing that differs. That is
 * deliberate on both counts. Shared, because the refusal is one rule about one plan and
 * two components would be two things to keep in step — the same argument the route gate
 * in src/router.tsx makes about two <Route>s under one check. Differentiated in the
 * heading, because the visitor arrived at a URL with a name on it: replacing "AI Genre
 * Mixer" with a generic "Composer+ required" page loses the answer to "where am I", which
 * is the mistake a redirect to /products would make more loudly.
 *
 * Presentation only, exactly like StudioLockedState next door: it takes the feature it is
 * standing in for and a callback, and renders. Which of the two states to render belongs
 * to the route gate, which is the one place that reads the entitlement. A component that
 * checked the flag itself would be a second gate to keep in step with the first.
 *
 * In this directory rather than in components/dna or components/mixer because it serves
 * both of those screens, and this is already where the pieces shared across the three
 * creation methods live — CreateMethods and ProcessingWave are here for the same reason.
 * Filing it under either feature would make one of its two screens import from the
 * other's folder.
 *
 * NOT A SECURITY BOUNDARY — the entitlement it reflects is a client-side boolean and
 * editable from devtools. The rule that actually matters, that no credit is spent and no
 * generation is started without the plan, is enforced separately in hooks/useSongs
 * (createSong refuses before it charges), so this screen being bypassed does not open a
 * path to generating. See the header of src/stores/entitlementsStore.ts.
 */
import { Dna, Shuffle } from 'lucide-react';
import { Btn, SectionHeading, UpgradePrompt } from '../ui';
import { formatUpgradeOfferPrice, upgradeOffer } from '../../data/upgradeOffers';
import { c } from '../../styles/theme';

/**
 * The redesigned Composer offer, shared with /products so locked pricing cannot drift.
 */
const COMPOSER_OFFER = upgradeOffer('music-dna-composer');

/** Which of the two Composer+ features the visitor was trying to reach. */
export type ComposerPlusFeature = 'dna' | 'mixer';

/**
 * Each feature's own heading — its icon, its h1 and its subtitle, verbatim from the
 * screen it replaces.
 *
 * Verbatim on purpose: the point of keeping the heading is that the page still identifies
 * itself, and a paraphrase of the real screen's subtitle would identify it slightly
 * wrongly. §3 forbids changing either layout, and this is the copy those layouts use.
 */
const FEATURES = {
  dna: {
    title: 'Music DNA',
    subtitle:
      'Paste a Spotify song link — Lyrixsa AI analyzes its musical DNA and turns it into a brand-new, original prompt',
    /** What this feature specifically is, for the prompt's description. */
    detail: 'analyze a song you already like and turn its musical DNA into an original prompt',
  },
  mixer: {
    title: 'AI Genre Mixer',
    subtitle:
      'Blend multiple music genres together and create completely original songs powered by AI.',
    detail: 'blend two or more genres into one original sound',
  },
} as const;

export interface ComposerPlusLockedStateProps {
  /** Which screen this is standing in for, so the heading is that screen's own. */
  feature: ComposerPlusFeature;
  /** Sends the visitor to the screen that sells Composer+. */
  onGetComposerPlus: () => void;
}

export default function ComposerPlusLockedState({
  feature,
  onGetComposerPlus,
}: ComposerPlusLockedStateProps) {
  const { title, subtitle, detail } = FEATURES[feature];
  /* Each screen's own icon — Dna on /dna, Shuffle on /genre-mixer — for the same
     reason the heading text is each screen's own. */
  const Icon = feature === 'dna' ? Dna : Shuffle;

  return (
    <div>
      <SectionHeading
        as="h1"
        icon={<Icon size={17} color={c.accent} />}
        title={title}
        subtitle={subtitle}
        marginBottom={18}
      />
      <UpgradePrompt
        paddingY={28}
        icon={<Icon size={28} color={c.text3} />}
        /* Names the plan, so the answer to "what do I need" is in the heading rather
           than only in the button. Both screens say the same sentence: it is one plan
           and one requirement, and two wordings would read as two restrictions. */
        title="Music DNA and Genre Mixer require Lyrixsa Composer+"
        /* Names what the plan unlocks — including the feature the visitor came for, by
           name — rather than only what is being withheld, and says plainly that the rest
           of the account is unaffected. Composer+ is one of six independent one-time
           products, not a tier that switches the app off, so someone without it still
           writes prompts on Create and still has every song they have made. */
        description={`Composer+ unlocks both AI creation methods — ${detail}. Writing your own prompts on Create, Image Music, and playing, downloading and organising your songs are not affected.`}
        action={
          <Btn onClick={onGetComposerPlus}>
            <Icon size={13} />
            Get {COMPOSER_OFFER?.product.name} — {formatUpgradeOfferPrice(COMPOSER_OFFER?.product ?? { price: 47 })}
          </Btn>
        }
      />
    </div>
  );
}
